import React, {
  useState,
  useEffect,
  useTransition,
  Suspense,
  lazy,
} from "react";
import { useTranslation } from "react-i18next";
import i18n from "@core/i18n";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FeatureRequest,
  FeatureStatus,
  FeatureCategory,
  FeatureRequestFilters,
  FeatureRequestWithVote,
} from "@core/types/featureRequests";
import { FeatureRequestService } from "@core/services/FeatureRequestService";
import { useAuth } from "@core/contexts/AuthContext";
import { ErrorBoundary } from "@core/components/ErrorBoundary";

// Lazy-load le formulaire modal pour isoler sa surface de suspension
// et éviter que le bundle principal grossisse inutilement.
const FeatureRequestForm = lazy(() =>
  import("../../components/featureRequests/FeatureRequestForm").then((m) => ({
    default: m.FeatureRequestForm,
  })),
);
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import {
  getProductConfig,
  type ProductType,
} from "@core/config/products.config";
import { vocab } from "@core/lib/productVocabulary";
import { toast } from "@core/components/ui/sonner";
import {
  Search,
  Filter,
  Plus,
  ThumbsUp,
  MessageSquare,
  ExternalLink,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  X,
} from "lucide-react";

const statusConfig = {
  proposed: {
    labelKey: "roadmap_status_proposed",
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    icon: AlertCircle,
  },
  under_review: {
    labelKey: "roadmap_status_under_review",
    color:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    icon: Clock,
  },
  planned: {
    labelKey: "roadmap_status_planned",
    color:
      "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
    icon: TrendingUp,
  },
  in_progress: {
    labelKey: "roadmap_status_in_progress",
    color:
      "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
    icon: AlertCircle,
  },
  completed: {
    labelKey: "roadmap_status_completed",
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    icon: CheckCircle,
  },
  rejected: {
    labelKey: "roadmap_status_rejected",
    color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    icon: XCircle,
  },
};

const categoryConfig = {
  export: {
    labelKey: "roadmap_category_export",
    color: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  integration: {
    labelKey: "roadmap_category_integration",
    color:
      "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  },
  ui: {
    labelKey: "roadmap_category_ui",
    color: "bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
  },
  mobile: {
    labelKey: "roadmap_category_mobile",
    color:
      "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  },
  performance: {
    labelKey: "roadmap_category_performance",
    color:
      "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  },
  security: {
    labelKey: "roadmap_category_security",
    color: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  },
  analytics: {
    labelKey: "roadmap_category_analytics",
    color:
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  },
  collaboration: {
    labelKey: "roadmap_category_collaboration",
    color: "bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  },
  other: {
    labelKey: "roadmap_category_other",
    color: "bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
  },
};

// Jetons d'accent par thème produit. Strings statiques complètes (pas de
// template `bg-${x}-600`) pour rester détectables par le purge Tailwind JIT.
// Seuls date (blue) et form (violet) exposent la roadmap ; fallback = blue.
const ROADMAP_ACCENTS: Record<
  string,
  {
    btn: string;
    ring: string;
    spinner: string;
    spinnerSm: string;
    pillActive: string;
    cardVoted: string;
    cardHover: string;
    titleHover: string;
    chipVoted: string;
  }
> = {
  blue: {
    btn: "bg-blue-600 hover:bg-blue-700",
    ring: "focus:ring-blue-500",
    spinner: "border-blue-600",
    spinnerSm: "border-blue-500",
    pillActive: "bg-blue-700 text-white hover:bg-blue-800",
    cardVoted:
      "bg-blue-900/10 border-2 border-blue-500/60 hover:border-blue-400",
    cardHover: "hover:border-blue-500/50",
    titleHover: "group-hover:text-blue-400 hover:text-blue-400",
    chipVoted: "bg-blue-600/30 text-blue-200 border border-blue-500/60",
  },
  violet: {
    btn: "bg-violet-600 hover:bg-violet-700",
    ring: "focus:ring-violet-500",
    spinner: "border-violet-600",
    spinnerSm: "border-violet-500",
    pillActive: "bg-violet-700 text-white hover:bg-violet-800",
    cardVoted:
      "bg-violet-900/10 border-2 border-violet-500/60 hover:border-violet-400",
    cardHover: "hover:border-violet-500/50",
    titleHover: "group-hover:text-violet-400 hover:text-violet-400",
    chipVoted: "bg-violet-600/30 text-violet-200 border border-violet-500/60",
  },
};

export const RoadmapPage: React.FC<{ productType?: ProductType }> = ({
  productType = "date",
}) => {
  const { t } = useTranslation();
  const A =
    ROADMAP_ACCENTS[getProductConfig(productType).theme] ??
    ROADMAP_ACCENTS.blue;
  const brand = vocab(productType).brand;
  useEffect(() => {
    document.title = `${t("seo_title_roadmap")} — ${brand}`;
  }, [t, brand]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isPending, startTransition] = useTransition();

  const [featureRequests, setFeatureRequests] = useState<
    FeatureRequestWithVote[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  // Pré-remplissage venant du widget support (?suggest=...)
  const [initialSuggestion, setInitialSuggestion] = useState<string>("");
  // Initialiser les filtres DIRECTEMENT depuis l'URL au 1er render :
  // le useEffect d'avant synchronisait trop tard (dans startTransition),
  // ce qui faisait tourner loadFeatureRequests() avec des defaults et
  // donnait toujours 1 résultat même avec ?status=completed.
  // Cf. Docs/TEST A FAIRE/EN COURS/2026-04-15-test-roadmap.md §A-2/3/5
  const [selectedStatus, setSelectedStatus] = useState<
    FeatureStatus | undefined
  >(() => (searchParams.get("status") as FeatureStatus) || undefined);
  const [selectedCategory, setSelectedCategory] = useState<
    FeatureCategory | undefined
  >(() => (searchParams.get("category") as FeatureCategory) || undefined);
  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get("search") || "",
  );
  const [sortBy, setSortBy] = useState<
    "created_at" | "votes_count" | "priority"
  >(() => {
    const s = searchParams.get("sort");
    if (s === "created_at" || s === "votes_count" || s === "priority") return s;
    return "votes_count";
  });
  // Pagination (P2.3) — batch de 50, offset croissant pour "Charger plus"
  const PAGE_SIZE = 50;
  const [loadedCount, setLoadedCount] = useState<number>(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const featureRequestService = FeatureRequestService.getInstance();

  // Sync state with URL params on mount
  useEffect(() => {
    // Les filtres (status/category/search/sort) sont déjà initialisés depuis
    // l'URL via les useState initializers ci-dessus. Ici on traite uniquement
    // le cas spécial ?suggest= qui ouvre le modal avec un texte pré-rempli.
    const suggest = searchParams.get("suggest");
    if (suggest) {
      startTransition(() => {
        setInitialSuggestion(suggest);
        setShowFeatureForm(true);
        const cleaned = new URLSearchParams(searchParams);
        cleaned.delete("suggest");
        setSearchParams(cleaned, { replace: true });
      });
    }
  }, []);

  useEffect(() => {
    loadFeatureRequests();
  }, [selectedStatus, selectedCategory, searchTerm, sortBy, i18n.language]);

  useEffect(() => {
    // Update URL params
    const params = new URLSearchParams();
    if (selectedStatus) params.set("status", selectedStatus);
    if (selectedCategory) params.set("category", selectedCategory);
    if (searchTerm) params.set("search", searchTerm);
    if (sortBy !== "votes_count") params.set("sort", sortBy);
    startTransition(() => setSearchParams(params));
  }, [selectedStatus, selectedCategory, searchTerm, sortBy]);

  const loadFeatureRequests = async () => {
    console.log("🔄 [ROADMAP] Loading feature requests with filters:", {
      status: selectedStatus,
      category: selectedCategory,
      search: searchTerm,
      sort: sortBy,
      language: i18n.language,
    });

    try {
      setLoading(true);
      setError(null);

      // On demande PAGE_SIZE + 1 pour détecter s'il reste d'autres pages
      // sans avoir besoin d'un COUNT séparé. Si on reçoit PAGE_SIZE+1, on
      // coupe le dernier élément et on affiche "Charger plus".
      const filters: FeatureRequestFilters = {
        status: selectedStatus,
        category: selectedCategory,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: "desc",
        limit: PAGE_SIZE + 1,
        language: i18n.language,
      };

      const {
        data,
        success,
        error: serviceError,
      } = await featureRequestService.getFeatureRequests(
        filters,
        user?.id,
        i18n.language,
      );

      if (!success || !data) {
        throw serviceError || new Error("Failed to load feature requests");
      }

      const more = data.length > PAGE_SIZE;
      const visible = more ? data.slice(0, PAGE_SIZE) : data;
      console.log(
        `✅ [ROADMAP] Loaded ${visible.length} feature requests (hasMore=${more})`,
      );
      // Pas de startTransition ici : ces setState ne déclenchent pas de
      // suspension et l'utilisateur attend le résultat de sa requête.
      // Cf. P3.1 cleanup.
      setFeatureRequests(visible);
      setLoadedCount(visible.length);
      setHasMore(more);
    } catch (err) {
      console.error("❌ [ROADMAP] Error loading feature requests:", err);
      setError(t("errors:roadmap_load_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (featureId: string, hasVoted: boolean) => {
    console.log(
      `Vote action: ${hasVoted ? "remove" : "add"} vote for feature ${featureId}`,
    );

    if (!user) {
      console.log("User not authenticated, redirecting to auth");
      // Redirect to login — /login est la route canonique dans date-polls
      // (/auth n'existe pas dans apps/date-polls/src/App.tsx)
      startTransition(() => navigate("/login?redirect=/roadmap"));
      return;
    }

    try {
      if (hasVoted) {
        await featureRequestService.removeVote(featureId, user.id);
        // Optimistic update : pas de suspension, setState direct. P3.1.
        setFeatureRequests((prev) =>
          prev.map((fr) =>
            fr.id === featureId
              ? {
                  ...fr,
                  user_has_voted: false,
                  votes_count: fr.votes_count - 1,
                }
              : fr,
          ),
        );
        console.log(`Vote removed for feature ${featureId}`);
        toast.success(t("roadmap_toast_vote_removed"));
      } else {
        await featureRequestService.voteForFeature(featureId, user.id);
        setFeatureRequests((prev) =>
          prev.map((fr) =>
            fr.id === featureId
              ? {
                  ...fr,
                  user_has_voted: true,
                  votes_count: fr.votes_count + 1,
                }
              : fr,
          ),
        );
        console.log(`Vote added for feature ${featureId}`);
        toast.success(t("roadmap_toast_vote_added"));
      }
    } catch (err) {
      console.error("Error voting:", err);
      setError(t("errors:roadmap_vote_failed"));
      toast.error(t("errors:roadmap_vote_failed"));
    }
  };

  /**
   * Ouvre le modal "Proposer une fonctionnalité".
   * Si l'utilisateur n'est pas connecté, redirige immédiatement vers
   * /login sans ouvrir le modal (évite de remplir le formulaire pour rien).
   * Cf. Docs/TEST A FAIRE/EN COURS/2026-04-15-test-roadmap.md §E-2
   */
  const openFeatureForm = () => {
    if (!user) {
      startTransition(() => navigate("/login?redirect=/roadmap"));
      return;
    }
    setShowFeatureForm(true);
  };

  /**
   * Charge la page suivante (offset = loadedCount) et l'append à la liste.
   * Cf. Ticket P2.3.
   */
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const filters: FeatureRequestFilters = {
        status: selectedStatus,
        category: selectedCategory,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: "desc",
        offset: loadedCount,
        limit: PAGE_SIZE + 1,
        language: i18n.language,
      };
      const res = await featureRequestService.getFeatureRequests(
        filters,
        user?.id,
        i18n.language,
      );
      if (res.success && res.data) {
        const more = res.data.length > PAGE_SIZE;
        const visible = more ? res.data.slice(0, PAGE_SIZE) : res.data;
        setFeatureRequests((prev) => [...prev, ...visible]);
        setLoadedCount((c) => c + visible.length);
        setHasMore(more);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFeatureCreated = () => {
    setShowFeatureForm(false);
    loadFeatureRequests(); // Rafraîchir la liste
  };

  const getStatusStats = () => {
    const stats = featureRequests.reduce(
      (acc, fr) => {
        acc[fr.status] = (acc[fr.status] || 0) + 1;
        return acc;
      },
      {} as Record<FeatureStatus, number>,
    );

    return Object.entries(statusConfig).map(([status, config]) => ({
      status: status as FeatureStatus,
      count: stats[status as FeatureStatus] || 0,
      ...config,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-[#0a0a0a] flex items-center justify-center">
        <div
          className={`animate-spin rounded-full h-12 w-12 border-b-2 ${A.spinner}`}
        ></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe bg-[#0a0a0a]">
      {/* Header */}
      <ProductHeader productType={productType} showAuth={true} />

      {/* Contenu principal avec sidebar */}
      <div className="flex">
        {/* Sidebar auto-suffisante avec son hamburger */}
        <ProductSidebar productType={productType} />

        {/* Contenu principal */}
        <div className="flex-1 min-w-0 pb-8">
          <div className="pt-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Header */}
              <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h1
                    role="heading"
                    className="text-xl sm:text-3xl font-bold tracking-tight text-white"
                  >
                    🗺️ {t("roadmap_title", { brand })}
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm text-gray-400 hidden sm:block">
                    {t("roadmap_subtitle", { brand })}
                  </p>
                  <div className="mt-2 text-sm text-gray-400">
                    {featureRequests.length}{" "}
                    {featureRequests.length <= 1
                      ? t("roadmap_feature_count_one")
                      : t("roadmap_feature_count_other")}{" "}
                    •{" "}
                    {(() => {
                      const total = featureRequests.reduce(
                        (sum, fr) => sum + fr.votes_count,
                        0,
                      );
                      return `${total} ${total <= 1 ? t("roadmap_vote_count_one") : t("roadmap_vote_count_other")}`;
                    })()}
                  </div>
                </div>

                <button
                  onClick={() => openFeatureForm()}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-2 ${A.btn} text-white rounded-lg transition-colors text-sm sm:text-base whitespace-nowrap`}
                  data-testid="roadmap-navigate"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    {t("roadmap_propose_feature")}
                  </span>
                  <span className="sm:hidden">{t("roadmap_propose")}</span>
                </button>
              </div>

              {/* Stats Bar - Mobile responsive */}
              <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto pb-2 sm:pb-0">
                  {getStatusStats().map(
                    ({ status, count, labelKey, color, icon: Icon }) => (
                      <button
                        key={status}
                        onClick={() =>
                          setSelectedStatus(
                            selectedStatus === status ? undefined : status,
                          )
                        }
                        className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                          selectedStatus === status
                            ? A.pillActive
                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }`}
                        data-testid="roadmap-button"
                      >
                        <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">
                          {t(labelKey)}
                          {selectedStatus === status && ` (${count})`}
                        </span>
                        <span className="sm:hidden">
                          {selectedStatus === status ? count : ""}
                        </span>
                      </button>
                    ),
                  )}
                </div>
              </div>

              {/* Filters and Search */}
              <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t("roadmap_search_placeholder")}
                      value={searchTerm}
                      onChange={(e) =>
                        startTransition(() => setSearchTerm(e.target.value))
                      }
                      data-testid="roadmap-search-input"
                      className={`w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${A.ring} text-white placeholder-gray-400`}
                    />
                  </div>

                  {/* Filtre catégorie (dropdown compact, au même niveau que
                      le tri) + tri. Cf. Ticket P2.7 : symétrie UX avec les
                      statuts plutôt que de cacher les catégories derrière
                      un "Filtres" drawer. */}
                  <div className="flex items-center gap-2 min-w-0">
                    <select
                      value={selectedCategory ?? ""}
                      onChange={(e) =>
                        startTransition(() =>
                          setSelectedCategory(
                            (e.target.value || undefined) as
                              | FeatureCategory
                              | undefined,
                          ),
                        )
                      }
                      className={`flex-1 min-w-0 px-3 sm:px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${A.ring} text-white text-sm sm:text-base`}
                      data-testid="roadmap-category-select"
                      aria-label={t("roadmap_filter_by_category")}
                    >
                      <option value="">{t("roadmap_all_categories")}</option>
                      {Object.entries(categoryConfig).map(
                        ([category, config]) => (
                          <option key={category} value={category}>
                            {t(config.labelKey)}
                          </option>
                        ),
                      )}
                    </select>

                    <select
                      value={sortBy}
                      onChange={(e) =>
                        startTransition(() => setSortBy(e.target.value as any))
                      }
                      className={`flex-1 min-w-0 px-3 sm:px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 ${A.ring} text-white text-sm sm:text-base`}
                      aria-label={t("roadmap_sort_by")}
                    >
                      <option value="votes_count">
                        {t("roadmap_sort_popular")}
                      </option>
                      <option value="created_at">
                        {t("roadmap_sort_recent")}
                      </option>
                      <option value="priority">
                        {t("roadmap_sort_priority")}
                      </option>
                    </select>
                  </div>
                </div>

                {/* Ancien drawer "Filtres" retiré (P2.7). Les catégories sont
                    désormais exposées directement via le select ci-dessus.
                    Bloc conservé en tant que no-op pour ne pas casser la
                    variable showFilters (conservée pour compatibilité). */}
                {false && showFilters && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {Object.entries(categoryConfig).map(
                      ([category, config]) => (
                        <button
                          key={category}
                          onClick={() =>
                            startTransition(() =>
                              setSelectedCategory(
                                selectedCategory === category
                                  ? undefined
                                  : (category as FeatureCategory),
                              ),
                            )
                          }
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            selectedCategory === category
                              ? A.pillActive
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          }`}
                          data-testid="roadmap-button"
                        >
                          {t(config.labelKey)}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-xl p-4 mb-6">
                  <p className="text-red-200">{error}</p>
                </div>
              )}

              {/* Feature Requests List */}
              <div className="space-y-4">
                {featureRequests.map((feature) => {
                  const featureStatusConfig = statusConfig[feature.status];
                  const featureCategoryConfig =
                    categoryConfig[feature.category || "other"];
                  const StatusIcon = featureStatusConfig.icon;

                  return (
                    <div
                      key={feature.id}
                      className={`rounded-xl p-4 sm:p-6 transition-colors group ${
                        feature.user_has_voted
                          ? A.cardVoted
                          : `bg-gray-800/30 border border-gray-700 ${A.cardHover}`
                      }`}
                      data-voted={feature.user_has_voted || undefined}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Title (cliquable → /roadmap/:id) */}
                          <div className="mb-3">
                            <button
                              type="button"
                              onClick={() =>
                                startTransition(() =>
                                  navigate(`/roadmap/${feature.id}`),
                                )
                              }
                              className="text-left w-full"
                              data-testid="roadmap-title-link"
                              aria-label={t("roadmap_view_details", {
                                title: feature.title,
                              })}
                            >
                              <h3
                                className={`text-lg font-semibold text-white ${A.titleHover} transition-colors break-words cursor-pointer`}
                              >
                                {feature.title}
                              </h3>
                            </button>
                          </div>

                          {/* Status and Category - Mobile responsive */}
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            {feature.user_has_voted && (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${A.chipVoted}`}
                                data-testid="roadmap-voted-badge"
                              >
                                <ThumbsUp className="w-3 h-3" />{" "}
                                {t("roadmap_voted")}
                              </span>
                            )}
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${featureStatusConfig.color}`}
                            >
                              <StatusIcon className="w-3 h-3" />
                              {t(featureStatusConfig.labelKey)}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${featureCategoryConfig.color}`}
                            >
                              {t(featureCategoryConfig.labelKey)}
                            </span>
                          </div>

                          {/* Description */}
                          <p className="text-gray-400 mb-4 line-clamp-3">
                            {feature.description}
                          </p>

                          {/* Metadata - Mobile responsive */}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-400">
                            <span className="whitespace-nowrap">
                              {feature.created_by
                                ? t("roadmap_proposed_by_user")
                                : t("roadmap_proposed_by_anonymous")}
                            </span>
                            <span className="hidden sm:inline">•</span>
                            <span className="whitespace-nowrap">
                              {new Date(feature.created_at).toLocaleDateString(
                                "fr-FR",
                              )}
                            </span>
                            {feature.votes_count > 0 && (
                              <>
                                <span className="hidden sm:inline">•</span>
                                <span className="whitespace-nowrap">
                                  {t("roadmap_votes_with_count", {
                                    count: feature.votes_count,
                                  })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Actions - Mobile responsive */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:ml-4 lg:flex-shrink-0">
                          <button
                            onClick={() =>
                              handleVote(
                                feature.id,
                                feature.user_has_voted || false,
                              )
                            }
                            aria-label={
                              !user
                                ? t("roadmap_sign_in_to_vote")
                                : feature.user_has_voted
                                  ? t("roadmap_remove_my_vote")
                                  : t("roadmap_vote")
                            }
                            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors text-sm ${A.btn} text-white`}
                            data-testid="roadmap-button"
                          >
                            <ThumbsUp className="w-4 h-4" />
                            <span className="hidden sm:inline">
                              {feature.user_has_voted
                                ? t("roadmap_voted")
                                : t("roadmap_vote")}
                            </span>
                            <span className="sm:hidden">
                              {feature.user_has_voted ? "✓" : "👍"}
                            </span>
                            {feature.votes_count > 0 && (
                              <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                                {feature.votes_count}
                              </span>
                            )}
                          </button>

                          <button
                            onClick={() =>
                              startTransition(() =>
                                navigate(`/roadmap/${feature.id}`),
                              )
                            }
                            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 ${A.btn} text-white rounded-lg transition-colors text-sm`}
                            data-testid="roadmap-navigate"
                          >
                            <MessageSquare className="w-4 h-4" />
                            <span className="hidden sm:inline">
                              {t("roadmap_details")}
                            </span>
                            <span className="sm:hidden">
                              {t("roadmap_see")}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Empty State */}
                {featureRequests.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <Search className="w-12 h-12 mx-auto" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">
                      {t("roadmap_empty_title")}
                    </h3>
                    <p className="text-gray-400 mb-6">
                      {t("roadmap_empty_description")}
                    </p>
                    <button
                      onClick={() => openFeatureForm()}
                      className={`inline-flex items-center gap-2 px-6 py-3 ${A.btn} text-white rounded-lg transition-colors`}
                      data-testid="roadmap-navigate"
                    >
                      <Plus className="w-4 h-4" />
                      {t("roadmap_propose_feature")}
                    </button>
                  </div>
                )}
              </div>

              {/* Load More (P2.3) */}
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className={`px-6 py-3 ${A.btn} text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid="roadmap-charger-plus-de-fonctionnalits"
                  >
                    {loadingMore ? t("loading") : t("roadmap_load_more")}
                  </button>
                </div>
              )}

              {/* Feature Request Form Modal — wrappé d'un ErrorBoundary et
                  d'un Suspense locaux. Cf. Docs/ARCHITECTURE/2026-04-15-
                  ROADMAP-FRAGILITE-ANALYSE.md : toute suspension ou exception
                  dans le formulaire doit être absorbée ici, pas au root, pour
                  éviter l'écran blanc global. */}
              {showFeatureForm && (
                <ErrorBoundary
                  fallback={
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                      <div className="bg-gray-800 border border-red-700 rounded-xl shadow-xl max-w-md w-full p-6 text-center">
                        <h2 className="text-xl font-bold text-white mb-2">
                          {t("errors:roadmap_error_occurred")}
                        </h2>
                        <p className="text-gray-300 mb-4">
                          {t("errors:roadmap_form_error_description")}
                        </p>
                        <button
                          onClick={() => setShowFeatureForm(false)}
                          className={`px-4 py-2 ${A.btn} text-white rounded-lg`}
                          data-testid="roadmap-fermer"
                        >
                          {t("close")}
                        </button>
                      </div>
                    </div>
                  }
                >
                  <Suspense
                    fallback={
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div
                          className={`animate-spin rounded-full h-12 w-12 border-b-2 ${A.spinnerSm}`}
                        />
                      </div>
                    }
                  >
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-white">
                              {t("roadmap_propose_feature")}
                            </h2>
                            <button
                              onClick={() => setShowFeatureForm(false)}
                              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                              data-testid="roadmap-button"
                            >
                              <X className="w-5 h-5 text-gray-400" />
                            </button>
                          </div>
                          <FeatureRequestForm
                            onSuccess={handleFeatureCreated}
                            onClose={() => setShowFeatureForm(false)}
                            initialDescription={initialSuggestion}
                          />
                        </div>
                      </div>
                    </div>
                  </Suspense>
                </ErrorBoundary>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapPage;
